import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Chart, KpiUnit } from '../dashboards.api';
import { CHROME, formatCompact, formatValue, niceTicks, OTHER_COLOR, seriesColor, SERIES_COLORS } from '../dashboards.utils';
import MaybeLink from './MaybeLink';

/* ── Shared plumbing ─────────────────────────────────────────────────────── */

const FONT = 12;
/** Rough width of one character at 12px, for fitting labels without measuring the DOM. */
const CHAR_W = 6.6;

/** Width of the element, following resizes, so the SVG is drawn at real pixels (text never scales). */
function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => setWidth(Math.floor(entries[0].contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

const fit = (text: string, px: number): string => {
  const max = Math.max(1, Math.floor(px / CHAR_W));
  return text.length <= max ? text : `${text.slice(0, Math.max(1, max - 1))}…`;
};

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Categories in first-seen order across all series, and each series' value per category. */
function normalise(chart: Chart) {
  const series = (chart.series ?? []).filter((s) => s && Array.isArray(s.points));
  const categories: string[] = [];
  const seen = new Set<string>();
  series.forEach((s) =>
    s.points.forEach((p) => {
      const label = String(p.label ?? '');
      if (!seen.has(label)) {
        seen.add(label);
        categories.push(label);
      }
    })
  );
  const values = series.map((s) => {
    const m = new Map(s.points.map((p) => [String(p.label ?? ''), p.value]));
    return categories.map((c) => (m.has(c) ? m.get(c) ?? null : null));
  });
  return { series, categories, values };
}

/** A bar growing right from x, square at the baseline and 4px-rounded at the data end. */
function hBarPath(x: number, y: number, w: number, h: number, roundEnd = true): string {
  if (w <= 0) return '';
  const r = roundEnd ? Math.min(4, w, h / 2) : 0;
  return `M${x},${y}H${x + w - r}${r ? `Q${x + w},${y} ${x + w},${y + r}` : ''}V${y + h - r}${
    r ? `Q${x + w},${y + h} ${x + w - r},${y + h}` : ''
  }H${x}Z`;
}

interface TipRow {
  color: string;
  name: string;
  value: string;
}
interface Tip {
  x: number;
  y: number;
  title: string;
  rows: TipRow[];
}

/** The one tooltip a chart shows: value first and strong, series name after, a line key for colour. */
function ChartTooltip({ tip, width }: { tip: Tip | null; width: number }) {
  if (!tip) return null;
  const left = Math.min(Math.max(tip.x + 12, 0), Math.max(0, width - 200));
  return (
    <Box
      role='status'
      sx={{
        position: 'absolute',
        left,
        top: Math.max(0, tip.y - 8),
        pointerEvents: 'none',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: 1.5,
        boxShadow: 3,
        px: 1.25,
        py: 0.75,
        minWidth: 120,
        maxWidth: 220,
        zIndex: 2,
      }}
    >
      <Typography variant='caption' color='text.secondary' component='div' sx={{ mb: 0.25 }}>
        {tip.title}
      </Typography>
      {tip.rows.map((r) => (
        <Stack key={r.name} direction='row' alignItems='center' gap={0.75}>
          <Box sx={{ width: 10, height: 2, borderRadius: 1, bgcolor: r.color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{r.value}</Typography>
          <Typography variant='caption' color='text.secondary' noWrap>
            {r.name}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
}

/** Legend for two or more series: a swatch shaped like the mark (line for LINE, square otherwise). */
function Legend({ names, line }: { names: string[]; line?: boolean }) {
  if (names.length < 2) return null;
  return (
    <Stack direction='row' flexWrap='wrap' columnGap={2} rowGap={0.5} sx={{ mb: 1 }}>
      {names.map((n, i) => (
        <Stack key={n} direction='row' alignItems='center' gap={0.75}>
          <Box
            sx={{
              width: line ? 14 : 10,
              height: line ? 2 : 10,
              borderRadius: line ? 1 : 0.5,
              bgcolor: seriesColor(i),
              flexShrink: 0,
            }}
          />
          <Typography variant='caption' color='text.secondary'>
            {n}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

/* ── BAR and STACKED: horizontal bars, one row per category ─────────────── */

export function BarChart({ chart, stacked }: { chart: Chart; stacked: boolean }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [tip, setTip] = useState<Tip | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const { series, categories, values } = useMemo(() => normalise(chart), [chart]);
  const names = series.map((s) => s.name);
  const unit = chart.unit;

  const totals = categories.map((_, ci) => values.reduce((sum, v) => sum + Math.max(0, num(v[ci])), 0));
  const max = stacked
    ? Math.max(0, ...totals)
    : Math.max(0, ...values.flatMap((v) => v.map((x) => Math.max(0, num(x)))));

  const grouped = !stacked && series.length > 1;
  const barH = stacked || !grouped ? 16 : Math.max(6, Math.min(12, Math.floor(28 / series.length)));
  const groupGap = 2;
  const bandInner = grouped ? series.length * barH + (series.length - 1) * groupGap : barH;
  const rowH = bandInner + 14;

  const labelW = Math.min(Math.max(...categories.map((c) => c.length * CHAR_W), 40) + 8, Math.max(80, width * 0.34));
  const valueW = Math.max(...(stacked ? totals : values.flat().map(num)).map((v) => formatValue(v, unit).length * CHAR_W), 24) + 8;
  const plotX = labelW;
  const plotW = Math.max(20, width - labelW - valueW);
  const scale = (v: number) => (max > 0 ? (Math.max(0, v) / max) * plotW : 0);
  const height = categories.length * rowH;

  const tipFor = (ci: number, y: number) => {
    const rows: TipRow[] = series.map((s, si) => ({
      color: seriesColor(si),
      name: s.name,
      value: formatValue(values[si][ci], unit),
    }));
    if (stacked && series.length > 1) rows.push({ color: 'transparent', name: 'Total', value: formatValue(totals[ci], unit) });
    return { x: plotX + Math.min(plotW, stacked ? scale(totals[ci]) : plotW / 2), y, title: categories[ci], rows };
  };

  return (
    <Box>
      <Legend names={names} />
      <Box ref={ref} sx={{ position: 'relative', width: '100%' }} onPointerLeave={() => (setTip(null), setHover(null))}>
        {width > 0 && (
          <svg width={width} height={height} role='img' aria-label={chart.title} style={{ display: 'block', overflow: 'visible' }}>
            {categories.map((cat, ci) => {
              const y0 = ci * rowH + (rowH - bandInner) / 2;
              const dim = hover !== null && hover !== cat;
              let acc = 0;
              return (
                <g
                  key={cat}
                  tabIndex={0}
                  aria-label={`${cat}: ${(stacked ? [formatValue(totals[ci], unit)] : values.map((v) => formatValue(v[ci], unit))).join(', ')}`}
                  onPointerEnter={() => {
                    setHover(cat);
                    setTip(tipFor(ci, ci * rowH));
                  }}
                  onFocus={() => {
                    setHover(cat);
                    setTip(tipFor(ci, ci * rowH));
                  }}
                  onBlur={() => (setTip(null), setHover(null))}
                  style={{ outline: 'none', cursor: 'default' }}
                >
                  {/* Hit target: the whole row, not just the painted bar. */}
                  <rect x={0} y={ci * rowH} width={width} height={rowH} fill='transparent' />
                  <text x={labelW - 8} y={ci * rowH + rowH / 2} dy='0.35em' textAnchor='end' fontSize={FONT} fill={CHROME.muted}>
                    <title>{cat}</title>
                    {fit(cat, labelW - 12)}
                  </text>
                  <line x1={plotX} x2={plotX} y1={ci * rowH + 4} y2={(ci + 1) * rowH - 4} stroke={CHROME.baseline} strokeWidth={1} />
                  <g opacity={dim ? 0.45 : 1}>
                    {stacked
                      ? series.map((_, si) => {
                          const w = scale(num(values[si][ci]));
                          const x = plotX + acc;
                          acc += w;
                          const last = values.slice(si + 1).every((v) => num(v[ci]) <= 0);
                          // 2px surface gap between touching segments.
                          const drawW = last ? w : Math.max(0, w - 2);
                          return <path key={si} d={hBarPath(x, y0, drawW, barH, last)} fill={seriesColor(si)} />;
                        })
                      : series.map((_, si) => (
                          <path
                            key={si}
                            d={hBarPath(plotX, y0 + si * (barH + groupGap), scale(num(values[si][ci])), barH)}
                            fill={seriesColor(si)}
                          />
                        ))}
                  </g>
                  {stacked ? (
                    <text
                      x={plotX + scale(totals[ci]) + 6}
                      y={y0 + barH / 2}
                      dy='0.35em'
                      fontSize={FONT}
                      fill='currentColor'
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {formatValue(totals[ci], unit)}
                    </text>
                  ) : (
                    series.map((_, si) => (
                      <text
                        key={si}
                        x={plotX + scale(num(values[si][ci])) + 6}
                        y={y0 + si * (barH + groupGap) + barH / 2}
                        dy='0.35em'
                        fontSize={grouped ? 11 : FONT}
                        fill={grouped ? CHROME.muted : 'currentColor'}
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {formatValue(values[si][ci], unit)}
                      </text>
                    ))
                  )}
                </g>
              );
            })}
          </svg>
        )}
        <ChartTooltip tip={tip} width={width} />
      </Box>
    </Box>
  );
}

/* ── LINE: categories over time, crosshair + one tooltip for every series ─ */

function LineChart({ chart }: { chart: Chart }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [idx, setIdx] = useState<number | null>(null);
  const { series, categories, values } = useMemo(() => normalise(chart), [chart]);
  const unit = chart.unit;
  const height = 220;

  const max = Math.max(0, ...values.flat().map(num));
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1] || 1;
  const tickW = Math.max(...ticks.map((t) => formatCompact(t, unit).length * CHAR_W)) + 10;
  const single = series.length === 1;
  const endLabel = single && values[0].length ? formatValue(values[0][values[0].length - 1], unit) : '';
  const padR = single ? Math.max(16, endLabel.length * CHAR_W + 12) : 12;
  const pad = { l: tickW, r: padR, t: 10, b: 26 };
  const plotW = Math.max(20, width - pad.l - pad.r);
  const plotH = height - pad.t - pad.b;
  const n = categories.length;
  const xAt = (i: number) => pad.l + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yAt = (v: number) => pad.t + plotH - (Math.max(0, v) / top) * plotH;

  // X labels: as many as fit, always the first and the last.
  const maxLabel = Math.max(...categories.map((c) => c.length), 1) * CHAR_W + 12;
  const every = Math.max(1, Math.ceil((n * maxLabel) / plotW));
  const showX = (i: number) => i === 0 || i === n - 1 || (i % every === 0 && n - 1 - i >= every / 2);

  const pathFor = (vals: (number | null)[]) => {
    let d = '';
    let pen = false;
    vals.forEach((v, i) => {
      if (v === null || v === undefined) {
        pen = false;
        return;
      }
      d += `${pen ? 'L' : 'M'}${xAt(i).toFixed(1)},${yAt(num(v)).toFixed(1)}`;
      pen = true;
    });
    return d;
  };

  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    const box = (e.currentTarget.ownerSVGElement ?? e.currentTarget).getBoundingClientRect();
    const x = e.clientX - box.left;
    const i = n <= 1 ? 0 : Math.round(((x - pad.l) / plotW) * (n - 1));
    setIdx(Math.max(0, Math.min(n - 1, i)));
  };
  const onKey = (e: React.KeyboardEvent<SVGSVGElement>) => {
    if (e.key === 'ArrowRight') setIdx((i) => Math.min(n - 1, (i ?? -1) + 1));
    else if (e.key === 'ArrowLeft') setIdx((i) => Math.max(0, (i ?? n) - 1));
  };

  const tip: Tip | null =
    idx === null
      ? null
      : {
          x: xAt(idx),
          y: pad.t,
          title: categories[idx],
          rows: series.map((s, si) => ({ color: seriesColor(si), name: s.name, value: formatValue(values[si][idx], unit) })),
        };

  return (
    <Box>
      <Legend names={series.map((s) => s.name)} line />
      <Box ref={ref} sx={{ position: 'relative', width: '100%' }}>
        {width > 0 && (
          <svg
            width={width}
            height={height}
            role='img'
            aria-label={`${chart.title}. Use the left and right arrow keys to read values.`}
            tabIndex={0}
            onKeyDown={onKey}
            onBlur={() => setIdx(null)}
            style={{ display: 'block', outline: 'none' }}
          >
            {ticks.map((t) => (
              <g key={t}>
                <line
                  x1={pad.l}
                  x2={pad.l + plotW}
                  y1={yAt(t)}
                  y2={yAt(t)}
                  stroke={t === 0 ? CHROME.baseline : CHROME.grid}
                  strokeWidth={1}
                />
                <text x={pad.l - 8} y={yAt(t)} dy='0.35em' textAnchor='end' fontSize={11} fill={CHROME.muted} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatCompact(t, unit)}
                </text>
              </g>
            ))}
            {categories.map((c, i) =>
              showX(i) ? (
                <text
                  key={c}
                  x={xAt(i)}
                  y={height - 8}
                  textAnchor={n > 1 && i === 0 ? 'start' : n > 1 && i === n - 1 ? 'end' : 'middle'}
                  fontSize={11}
                  fill={CHROME.muted}
                >
                  {c}
                </text>
              ) : null
            )}
            {single && values[0].some((v) => v !== null) && (
              <path
                d={`${pathFor(values[0])}L${xAt(n - 1)},${yAt(0)}L${xAt(0)},${yAt(0)}Z`}
                fill={seriesColor(0)}
                opacity={0.1}
              />
            )}
            {values.map((vals, si) => (
              <path
                key={si}
                d={pathFor(vals)}
                fill='none'
                stroke={seriesColor(si)}
                strokeWidth={2}
                strokeLinejoin='round'
                strokeLinecap='round'
              />
            ))}
            {single && n > 0 && values[0][n - 1] !== null && (
              <>
                <circle cx={xAt(n - 1)} cy={yAt(num(values[0][n - 1]))} r={4} fill={seriesColor(0)} stroke={CHROME.surface} strokeWidth={2} />
                <text x={xAt(n - 1) + 8} y={yAt(num(values[0][n - 1]))} dy='0.35em' fontSize={FONT} fill='currentColor'>
                  {endLabel}
                </text>
              </>
            )}
            {idx !== null && (
              <g pointerEvents='none'>
                <line x1={xAt(idx)} x2={xAt(idx)} y1={pad.t} y2={pad.t + plotH} stroke={CHROME.baseline} strokeWidth={1} />
                {values.map((vals, si) =>
                  vals[idx] === null ? null : (
                    <circle key={si} cx={xAt(idx)} cy={yAt(num(vals[idx]))} r={4} fill={seriesColor(si)} stroke={CHROME.surface} strokeWidth={2} />
                  )
                )}
              </g>
            )}
            <rect
              x={pad.l - 8}
              y={0}
              width={plotW + 16}
              height={height}
              fill='transparent'
              onPointerMove={onMove}
              onPointerLeave={() => setIdx(null)}
            />
          </svg>
        )}
        <ChartTooltip tip={tip} width={width} />
      </Box>
    </Box>
  );
}

/* ── DONUT: parts of a whole, first series only ──────────────────────────── */

function DonutChart({ chart }: { chart: Chart }) {
  const [hover, setHover] = useState<number | null>(null);
  const unit = chart.unit;
  const slices = useMemo(() => {
    const pts = (chart.series?.[0]?.points ?? []).map((p) => ({ label: String(p.label ?? ''), value: Math.max(0, num(p.value)) }));
    // Past the eighth slice, the rest fold into "Other" rather than take a generated hue.
    if (pts.length <= SERIES_COLORS.length) return pts.map((p, i) => ({ ...p, color: seriesColor(i) }));
    const head = pts.slice(0, SERIES_COLORS.length - 1).map((p, i) => ({ ...p, color: seriesColor(i) }));
    const rest = pts.slice(SERIES_COLORS.length - 1).reduce((s, p) => s + p.value, 0);
    return [...head, { label: 'Other', value: rest, color: OTHER_COLOR }];
  }, [chart]);
  const total = slices.reduce((s, p) => s + p.value, 0);

  const size = 168;
  const r = size / 2;
  const inner = r - 26;
  let angle = -Math.PI / 2;
  const arcs = slices.map((s) => {
    const sweep = total > 0 ? (s.value / total) * Math.PI * 2 : 0;
    const a0 = angle;
    angle += sweep;
    return { ...s, a0, a1: angle, sweep };
  });

  const arcPath = (a0: number, a1: number) => {
    if (a1 - a0 >= Math.PI * 2 - 1e-6) {
      // A full ring: two half arcs.
      return `M${r},${r - r}A${r},${r} 0 1 1 ${r},${r + r}A${r},${r} 0 1 1 ${r},0ZM${r},${r - inner}A${inner},${inner} 0 1 0 ${r},${r + inner}A${inner},${inner} 0 1 0 ${r},${r - inner}Z`;
    }
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const p = (rad: number, a: number) => `${(r + rad * Math.cos(a)).toFixed(2)},${(r + rad * Math.sin(a)).toFixed(2)}`;
    return `M${p(r, a0)}A${r},${r} 0 ${large} 1 ${p(r, a1)}L${p(inner, a1)}A${inner},${inner} 0 ${large} 0 ${p(inner, a0)}Z`;
  };

  const focus = hover !== null ? arcs[hover] : null;
  const pct = (v: number) => (total > 0 ? `${Math.round((v / total) * 100)}%` : '—');

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems='center' gap={3}>
      <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} role='img' aria-label={chart.title} style={{ display: 'block' }}>
          {arcs.map((a, i) =>
            a.sweep > 0 ? (
              <path
                key={a.label}
                d={arcPath(a.a0, a.a1)}
                fill={a.color}
                fillRule='evenodd'
                stroke={CHROME.surface}
                strokeWidth={2}
                opacity={hover !== null && hover !== i ? 0.45 : 1}
                tabIndex={0}
                aria-label={`${a.label}: ${formatValue(a.value, unit)} (${pct(a.value)})`}
                onPointerEnter={() => setHover(i)}
                onPointerLeave={() => setHover(null)}
                onFocus={() => setHover(i)}
                onBlur={() => setHover(null)}
                style={{ outline: 'none' }}
              />
            ) : null
          )}
        </svg>
        <Stack
          alignItems='center'
          justifyContent='center'
          sx={{ position: 'absolute', inset: 26, pointerEvents: 'none', textAlign: 'center' }}
        >
          <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.2 }}>
            {formatValue(focus ? focus.value : total, unit)}
          </Typography>
          <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1.2, maxWidth: 100 }} noWrap>
            {focus ? focus.label : 'Total'}
          </Typography>
        </Stack>
      </Box>
      <Stack gap={0.5} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        {arcs.map((a, i) => (
          <Stack
            key={a.label}
            direction='row'
            alignItems='center'
            gap={1}
            onPointerEnter={() => setHover(i)}
            onPointerLeave={() => setHover(null)}
            sx={{ opacity: hover !== null && hover !== i ? 0.6 : 1 }}
          >
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: a.color, flexShrink: 0 }} />
            <Typography variant='body2' sx={{ flex: 1, minWidth: 0 }} noWrap title={a.label}>
              {a.label}
            </Typography>
            <Typography variant='body2' sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {formatValue(a.value, unit)}
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ width: 36, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              {pct(a.value)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

/* ── Table view (every value reachable without hovering) ────────────────── */

function ChartTable({ chart }: { chart: Chart }) {
  const { series, categories, values } = useMemo(() => normalise(chart), [chart]);
  const unit: KpiUnit | null = chart.unit;
  const cell = { px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'grey.300', fontSize: '0.8125rem' } as const;
  return (
    <Box sx={{ overflowX: 'auto', maxHeight: 280 }}>
      <Box component='table' sx={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <Box component='th' sx={{ ...cell, textAlign: 'left', color: 'text.secondary', fontWeight: 500 }} />
            {series.map((s) => (
              <Box component='th' key={s.name} sx={{ ...cell, textAlign: 'right', color: 'text.secondary', fontWeight: 500 }}>
                {s.name}
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {categories.map((c, ci) => (
            <tr key={c}>
              <Box component='td' sx={cell}>
                {c}
              </Box>
              {series.map((s, si) => (
                <Box component='td' key={s.name} sx={{ ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {formatValue(values[si][ci], unit)}
                </Box>
              ))}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

/* ── The card ────────────────────────────────────────────────────────────── */

export function ChartFrame({ title, action, children }: { title: ReactNode; action?: ReactNode; children: ReactNode }) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: 2,
        bgcolor: 'background.paper',
        p: 2,
        minWidth: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Stack direction='row' alignItems='center' gap={1} sx={{ minHeight: 28 }}>
        <Typography variant='subtitle2' component='h3' sx={{ flex: 1, minWidth: 0, fontSize: '0.9375rem' }}>
          {title}
        </Typography>
        {action}
      </Stack>
      <Box sx={{ flex: 1, minWidth: 0, color: 'text.primary' }}>{children}</Box>
    </Box>
  );
}

/**
 * A dashboard chart, drawn with inline SVG: BAR (horizontal bars per category, grouped when
 * several series), STACKED (one bar per category, a segment per series), LINE (over time, with a
 * crosshair) and DONUT (parts of a whole). Each has a table view behind the toggle.
 */
export default function ChartCard({ chart }: { chart: Chart }) {
  const [asTable, setAsTable] = useState(false);
  const empty = !(chart.series ?? []).some((s) => (s.points ?? []).some((p) => p.value !== null && p.value !== undefined));

  const title = (
    <MaybeLink to={chart.link}>
      {chart.title}
      {chart.link && <Icon icon='mdi:arrow-top-right' width={14} style={{ verticalAlign: 'middle', marginLeft: 4, opacity: 0.6 }} />}
    </MaybeLink>
  );

  const toggle = empty ? null : (
    <Tooltip title={asTable ? 'Show chart' : 'Show as table'}>
      <IconButton size='small' onClick={() => setAsTable((t) => !t)} aria-label={asTable ? 'Show chart' : 'Show as table'}>
        <Icon icon={asTable ? 'mdi:chart-bar' : 'mdi:table'} width={18} />
      </IconButton>
    </Tooltip>
  );

  let body: ReactNode;
  if (empty) {
    body = (
      <Stack alignItems='center' justifyContent='center' sx={{ py: 5, color: 'text.secondary' }} gap={1}>
        <Icon icon='mdi:chart-box-outline' width={28} />
        <Typography variant='body2'>No data yet</Typography>
      </Stack>
    );
  } else if (asTable) body = <ChartTable chart={chart} />;
  else if (chart.kind === 'LINE') body = <LineChart chart={chart} />;
  else if (chart.kind === 'DONUT') body = <DonutChart chart={chart} />;
  else body = <BarChart chart={chart} stacked={chart.kind === 'STACKED'} />;

  return (
    <ChartFrame title={title} action={toggle}>
      {body}
    </ChartFrame>
  );
}

export function ChartCardSkeleton() {
  return (
    <ChartFrame title={<Skeleton variant='text' width='45%' />}>
      <Stack gap={1.25} sx={{ pt: 1 }}>
        {[80, 62, 48, 35, 22].map((w) => (
          <Stack key={w} direction='row' gap={1} alignItems='center'>
            <Skeleton variant='text' width={70} />
            <Skeleton variant='rounded' height={14} width={`${w}%`} />
          </Stack>
        ))}
      </Stack>
    </ChartFrame>
  );
}
